import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import BusinessIcon from '@mui/icons-material/Business';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { CompanyDetail, CompanyUpdate } from '../../types';

const CompanyInfoPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { managementToken } = useManagementAuth();
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<CompanyUpdate>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const scrollToField = (fieldName: string) => {
    const element = document.querySelector<HTMLElement>(`[name="${fieldName}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  };

  useEffect(() => {
    const fetchCompany = async () => {
      if (!managementToken || !companyId) return;
      try {
        const data = await api.managementCompany.getById(companyId, managementToken);
        setCompany(data);
        setEditData({
          name: data.name,
          address: data.address || '',
          phone: data.phone || '',
          representative_name: data.representative_name || '',
          business_type: data.business_type || '',
        });
      } catch (error) {
        console.error('企業情報の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [managementToken, companyId]);

  const handleSave = async () => {
    if (!managementToken || !companyId) return;

    const errors: Record<string, string> = {};
    if (!editData.name?.trim()) errors.name = '企業名は必須です';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});

    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.managementCompany.update(companyId, managementToken, editData);
      setCompany(updated);
      setIsEditMode(false);
      setMessage({ type: 'success', text: '企業情報を更新しました' });
    } catch {
      setMessage({ type: 'error', text: '更新に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      setEditData({
        name: company.name,
        address: company.address || '',
        phone: company.phone || '',
        representative_name: company.representative_name || '',
        business_type: company.business_type || '',
      });
    }
    setIsEditMode(false);
    setFieldErrors({});
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return <Typography color="error">企業情報が見つかりません</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
        企業情報
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardHeader
          avatar={<BusinessIcon color="primary" />}
          title="基本情報"
          action={
            !isEditMode ? (
              <Button variant="outlined" size="small" onClick={() => { setFieldErrors({}); setIsEditMode(true); }}>
                編集
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={handleCancel} disabled={saving}>
                  キャンセル
                </Button>
                <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : '保存'}
                </Button>
              </Box>
            )
          }
        />
        <CardContent>
          {!isEditMode ? (
            <Box>
              <InfoRow label="企業名" value={company.name} />
              <InfoRow label="所在地" value={company.address} />
              <InfoRow label="電話番号" value={company.phone} />
              <InfoRow label="代表者名" value={company.representative_name} />
              <InfoRow label="業種" value={company.business_type} />
            </Box>
          ) : (
            <Box>
              <TextField
                name="name"
                label="企業名" fullWidth margin="normal"
                value={editData.name || ''}
                onChange={(e) => {
                  setEditData({ ...editData, name: e.target.value });
                  if (fieldErrors.name) setFieldErrors((prev) => { const { name, ...rest } = prev; return rest; });
                }}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
              <TextField
                name="address"
                label="所在地" fullWidth margin="normal"
                value={editData.address || ''} onChange={(e) => setEditData({ ...editData, address: e.target.value })}
              />
              <TextField
                name="phone"
                label="電話番号" fullWidth margin="normal"
                value={editData.phone || ''} onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
              <TextField
                name="representative_name"
                label="代表者名" fullWidth margin="normal"
                value={editData.representative_name || ''} onChange={(e) => setEditData({ ...editData, representative_name: e.target.value })}
              />
              <TextField
                name="business_type"
                label="業種" fullWidth margin="normal"
                value={editData.business_type || ''} onChange={(e) => setEditData({ ...editData, business_type: e.target.value })}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ pl: 0 }}>
      {value || '-'}
    </Typography>
  </Box>
);

export default CompanyInfoPage;
